import { Component, createContext, useContext } from "react";
import ReactDOM from "react-dom";

import { getDisplayName } from "../Utils/getDisplayName";
import withAlert from "../Alerts/withAlert";
import { Consumer as AppContainerConsumer } from "../Main/AppContainerContext";
import AuthenticationManager from "../Authentication/AuthenticationManager";

export const AuthenticationServiceContext = createContext({});

class AuthenticationServiceBase extends Component {
  static displayName = "AuthenticationService";

  state = { isAuthenticationShown: false, additionalProps: {} };
  authorization = null;

  constructor() {
    super();

    this.displayAuthenticationManager = this.displayAuthenticationManager.bind(this);
    this.authenticate = this.authenticate.bind(this);
    this.onAuthenticated = this.onAuthenticated.bind(this);
    this.onAuthenticationManagerHidden = this.onAuthenticationManagerHidden.bind(this);
  }

  get providedContext() {
    return {
      authenticate: this.authenticate,
      displayAuthenticationManager: this.displayAuthenticationManager,
      isAuthenticationShown: this.state.isAuthenticationShown,
      requiresAuth: this.requiresAuth
    };
  }

  get requiresAuth() {
    return this.authorization === "SIGNIN" || this.authorizeAnonymousUserAuth;
  }

  get authorizeAnonymousUserAuth() {
    return this.authorization === "WITH_VIEWER";
  }

  get authenticationManager() {
    const { isAuthenticationShown, additionalProps } = this.state;

    if (!isAuthenticationShown) return null;

    return ReactDOM.createPortal(
      <AuthenticationManager
        onClose={this.onAuthenticationManagerHidden}
        onSignIn={this.onAuthenticated}
        authorizeAnonymousUserAuth={this.authorizeAnonymousUserAuth}
        authenticationAdditionalProps={additionalProps}
      />,
      this.props.appContainer
    );
  }

  showSignInMessage(signInMessage) {
    // Display a sign in message when the user clicks the button to explain
    // the reason he has to sign in.
    //
    if (signInMessage) {
      this.props.showAlert({
        message: signInMessage,
        type: "info"
      });
    }
  }

  displayAuthenticationManager(onAuthenticated) {
    this.authenticate("SIGNIN", onAuthenticated);
  }

  authenticate(authorization, onAuthenticated, additionalProps, signInMessage) {
    this.authorization = authorization;

    if (this.requiresAuth) {
      this.showSignInMessage(signInMessage);

      this.setState({
        isAuthenticationShown: true,
        additionalProps,
        onAuthenticated
      });
    } else {
      onAuthenticated && onAuthenticated();
    }
  }

  onAuthenticated() {
    if (this.state.onAuthenticated) {
      this.state.onAuthenticated();
    }

    this.onAuthenticationManagerHidden();
  }

  onAuthenticationManagerHidden() {
    this.setState({
      isAuthenticationShown: false,
      onAuthenticated: null,
      additionalProps: {}
    });
  }

  render() {
    return (
      <AuthenticationServiceContext.Provider value={this.providedContext}>
        {this.props.children}
        {this.authenticationManager}
      </AuthenticationServiceContext.Provider>
    );
  }
}

export const AuthenticationService = withAlert(props => {
  return (
    <AppContainerConsumer>
      {appContainer => (
        <AuthenticationServiceBase {...props} appContainer={appContainer} />
      )}
    </AppContainerConsumer>
  );
});

export const useAuthenticationManager = () => {
  return useContext(AuthenticationServiceContext);
};

export function withAuthenticationManager(WrappedComponent) {
  const WithAuthenticationManager = props => {
    const { authenticate, ...context } = useAuthenticationManager();

    const decoratedAuthenticate = onAuthenticated => {
      const { authorization, signInMessage } = props;
      const additionalProps = props.additionalProps || {};

      authenticate(authorization, onAuthenticated, additionalProps, signInMessage);
    };

    return (
      <WrappedComponent {...props} {...context} authenticate={decoratedAuthenticate} />
    );
  };

  WithAuthenticationManager.displayName = `WithAuthenticationManager(${getDisplayName(
    WrappedComponent
  )})`;

  return WithAuthenticationManager;
}
